import { interpreterStoreAtom, runtimeApplicationAtom } from "@/store";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAtomValue } from "jotai";
import {
  useBpmDatasetRecords,
  useBpmDatasetRecordsByValues,
  usePreloadMasterDataTableKeys,
} from "./useMasterData";
import { useCodeHelper } from "./useCode/useCodeHelper";
import {
  extractReferencedFieldNames,
  extractReferencedMasterDataTableNames,
} from "./useCode/utils";
import type { SelectOption } from "@ui/select";
import type { Operator } from "@/types/flow";
import type { FormSchema } from "@/types/domain";
import type { DatasourceValue } from "@/components/form/attributes/datasource/component";
import type {
  DynamicFilterExpression,
  DynamicFilterUi,
  DynamicValue,
} from "@/components/form/attributes/datasource/dynamic-setting";
import type { StaticValue } from "@/components/form/attributes/datasource/static-setting";

const resolveFieldIdByName = (fieldName: string, iStore: any) => {
  const entities = iStore?.schema?.entities ?? {};

  return (
    Object.entries(entities).find(
      ([, entity]) =>
        (entity as { attributes?: { name?: string } }).attributes?.name ===
        fieldName,
    )?.[0] ??
    (Object.prototype.hasOwnProperty.call(entities, fieldName)
      ? fieldName
      : undefined)
  );
};

export function useSubscribeFields(fieldNames: string[]): number {
  const iStore = useAtomValue(interpreterStoreAtom);
  const [tick, setTick] = useState(0);

  const namesKey = Array.from(
    new Set(fieldNames.map((n) => n.trim()).filter(Boolean)),
  )
    .sort()
    .join("|");

  useEffect(() => {
    if (!iStore || !namesKey) return;
    const names = namesKey.split("|");
    const ids = new Set<string>();
    names.forEach((name) => {
      const id = resolveFieldIdByName(name, iStore);
      if (id) ids.add(id);
    });
    if (ids.size === 0) return;

    return iStore.subscribe((_data: unknown, events: any[]) => {
      const matched = events.some(
        (e) =>
          e?.name === "EntityValueUpdated" && ids.has(e?.payload?.entityId),
      );
      if (matched) {
        setTick((t) => t + 1);
      }
    });
  }, [iStore, namesKey]);

  return tick;
}

export function useSubscribeField(fieldName?: string): unknown {
  const iStore = useAtomValue(interpreterStoreAtom);
  const normalizedFieldName = useMemo(() => fieldName?.trim(), [fieldName]);

  const [value, setValue] = useState<unknown>(() => {
    if (!normalizedFieldName || !iStore) return undefined;
    const fieldId = resolveFieldIdByName(normalizedFieldName, iStore);
    const values = iStore.getEntitiesValues?.() ?? {};
    if (fieldId && Object.prototype.hasOwnProperty.call(values, fieldId))
      return values[fieldId];
    if (Object.prototype.hasOwnProperty.call(values, normalizedFieldName))
      return values[normalizedFieldName];
    return undefined;
  });

  useEffect(() => {
    if (!normalizedFieldName || !iStore) {
      setValue(undefined);
      return;
    }

    const fieldId = resolveFieldIdByName(normalizedFieldName, iStore);
    const getCurrentValue = () => {
      const values = iStore.getEntitiesValues?.() ?? {};

      if (fieldId && Object.prototype.hasOwnProperty.call(values, fieldId)) {
        return values[fieldId];
      }

      if (Object.prototype.hasOwnProperty.call(values, normalizedFieldName)) {
        return values[normalizedFieldName];
      }

      return undefined;
    };

    setValue(getCurrentValue());

    const unsubscribe = iStore.subscribe((_data: unknown, events: any[]) => {
      const shouldUpdate =
        !fieldId ||
        events.some(
          (event) =>
            event?.name === "EntityValueUpdated" &&
            event?.payload?.entityId === fieldId,
        );

      if (shouldUpdate) {
        setValue(getCurrentValue());
      }
    });

    return unsubscribe;
  }, [iStore, normalizedFieldName]);

  return value;
}

const isUiFilter = (
  filter: DynamicValue["filter"],
): filter is DynamicFilterUi =>
  Boolean(filter && "columnKey" in filter && "formField" in filter);

const isExpressionFilter = (
  filter: DynamicValue["filter"],
): filter is DynamicFilterExpression =>
  Boolean(filter && "expression" in filter);

const parseStringArray = (value: string): string[] | undefined => {
  const trimmed = value.trim();
  if (!(trimmed.startsWith("[") && trimmed.endsWith("]"))) return undefined;

  try {
    const parsed = JSON.parse(trimmed);
    if (Array.isArray(parsed)) {
      return parsed.filter((item): item is string => typeof item === "string");
    }
  } catch {
    return undefined;
  }

  return undefined;
};

const normalizeDefaultValue = (
  defaultValue: StaticValue["defaultValue"] | undefined,
): string | string[] | undefined => {
  const raw =
    defaultValue &&
    typeof defaultValue === "object" &&
    "isReference" in defaultValue
      ? defaultValue.value
      : defaultValue;

  if (typeof raw === "string") return parseStringArray(raw) ?? raw;
  if (Array.isArray(raw)) {
    return raw.filter((item): item is string => typeof item === "string");
  }
  return undefined;
};

const resolveSingleValue = (
  entityValue: unknown,
  normalizedDefault: string | string[] | undefined,
): string => {
  if (typeof entityValue === "string") return entityValue;

  if (Array.isArray(entityValue)) {
    const first = entityValue[0];
    if (typeof first === "string") return first;
    if (Array.isArray(normalizedDefault)) return normalizedDefault[0] ?? "";
    return typeof normalizedDefault === "string" ? normalizedDefault : "";
  }

  if (Array.isArray(normalizedDefault)) return normalizedDefault[0] ?? "";
  return typeof normalizedDefault === "string" ? normalizedDefault : "";
};

const resolveMultipleValue = (
  entityValue: unknown,
  normalizedDefault: string | string[] | undefined,
): string[] => {
  if (Array.isArray(entityValue)) {
    return entityValue.filter(
      (item): item is string => typeof item === "string",
    );
  }

  if (typeof entityValue === "string") {
    return (
      parseStringArray(entityValue) ??
      (entityValue.trim() ? [entityValue.trim()] : [])
    );
  }

  if (Array.isArray(normalizedDefault)) return normalizedDefault;
  return typeof normalizedDefault === "string" && normalizedDefault
    ? [normalizedDefault]
    : [];
};

const resolveSelectedOptionValues = (
  selectedValue: unknown,
  normalizedDefault: string | string[] | undefined,
): string[] => {
  if (Array.isArray(selectedValue)) {
    return selectedValue.filter(
      (item): item is string => typeof item === "string" && item !== "",
    );
  }

  if (typeof selectedValue === "string" && selectedValue !== "") {
    return parseStringArray(selectedValue) ?? [selectedValue];
  }

  if (Array.isArray(normalizedDefault)) return normalizedDefault;
  return typeof normalizedDefault === "string" && normalizedDefault
    ? [normalizedDefault]
    : [];
};

const compareWithOperator = (
  operator: Operator | undefined,
  left: unknown,
  right: unknown,
): boolean => {
  if (!operator) return true;

  const leftNum = Number(left);
  const rightNum = Number(right);
  const numeric = Number.isFinite(leftNum) && Number.isFinite(rightNum);

  switch (operator) {
    case ">":
      return numeric ? leftNum > rightNum : String(left) > String(right);
    case "<":
      return numeric ? leftNum < rightNum : String(left) < String(right);
    case ">=":
      return numeric ? leftNum >= rightNum : String(left) >= String(right);
    case "<=":
      return numeric ? leftNum <= rightNum : String(left) <= String(right);
    case "==":
    case "equals":
      return String(left ?? "") === String(right ?? "");
    case "!=":
    case "not_equals":
      return String(left ?? "") !== String(right ?? "");
    case "contains":
      return String(left ?? "").includes(String(right ?? ""));
    case "not_contains":
      return !String(left ?? "").includes(String(right ?? ""));
    default:
      return false;
  }
};

const mapRecordsToOptions = (
  records: Record<string, unknown>[],
  labelKey: string,
  valueKey: string,
): SelectOption<string>[] => {
  const seen = new Set<string>();
  const options: SelectOption<string>[] = [];

  records.forEach((row) => {
    const value = String(row[valueKey] ?? "").trim();
    if (!value || seen.has(value)) return;
    seen.add(value);
    options.push({ label: String(row[labelKey] ?? ""), value, key: value });
  });

  return options;
};

const isEmptyFilterValue = (value: unknown): boolean => {
  if (value === undefined || value === null) return true;
  if (typeof value === "string") return value.trim() === "";
  return false;
};

const normalizeSelectValue = (value: unknown): string =>
  String(value ?? "").trim();

export const normalizeMultiSelectInput = (
  value: string | string[] | undefined,
): string[] => {
  if (Array.isArray(value)) return value.filter((item) => item !== "");
  return value ? [value] : [];
};

export function useSelectFieldResolvedValues(params: {
  datasource: DatasourceValue | undefined;
  entityValue: unknown;
}) {
  const { datasource, entityValue } = params;
  const normalizedDefault = useMemo(
    () => normalizeDefaultValue(datasource?.defaultValue),
    [datasource],
  );

  const singleValue = useMemo(
    () => resolveSingleValue(entityValue, normalizedDefault),
    [entityValue, normalizedDefault],
  );
  const multipleValue = useMemo(
    () => resolveMultipleValue(entityValue, normalizedDefault),
    [entityValue, normalizedDefault],
  );

  return { singleValue, multipleValue };
}

export function useSelectFieldOptions(params: {
  datasource: DatasourceValue | undefined;
  fieldName: string;
  getFormFieldValueByName: (name: string) => unknown;
  optionSearchQuery?: string;
  selectedValue: unknown;
  enableBackendSearch?: boolean;
  disableFiltering?: boolean;
}) {
  const {
    datasource,
    fieldName,
    getFormFieldValueByName,
    optionSearchQuery,
    selectedValue,
    enableBackendSearch = false,
    disableFiltering = false,
  } = params;
  const iStore = useAtomValue(interpreterStoreAtom);
  const runtimeApplication = useAtomValue(runtimeApplicationAtom);
  const dynamicDatasource =
    datasource?.type === "dynamic" ? datasource : undefined;
  // Stable references so useCodeHelper's internal useCallback memoization holds.
  // useCodeHelper's getFormField only consults formSchema/formData when iStore
  // is null (compilation phase). At runtime iStore is always present, so empty
  // stand-ins are safe and prevent every render from recreating executeCode.
  const stableFormSchema = useMemo<FormSchema>(
    () => ({ root: [], entities: {} }),
    [],
  );
  const stableFormData = useMemo<Record<string, unknown>>(() => ({}), []);
  const { executeCode } = useCodeHelper({
    formSchema: stableFormSchema,
    formData: stableFormData,
    application: runtimeApplication,
  });

  const tableKey = dynamicDatasource?.table?.tableKey?.trim();
  const labelKey = dynamicDatasource?.table?.labelKey?.trim();
  const valueKey = dynamicDatasource?.table?.valueKey?.trim();
  const isDynamicReady = Boolean(tableKey && labelKey && valueKey);
  const trimmedOptionSearchQuery = optionSearchQuery?.trim() ?? "";
  const normalizedDefault = useMemo(
    () => normalizeDefaultValue(datasource?.defaultValue),
    [datasource?.defaultValue],
  );

  const watchedFilterFieldName =
    !disableFiltering &&
    dynamicDatasource?.filter &&
    isUiFilter(dynamicDatasource.filter)
      ? dynamicDatasource.filter.formField?.trim()
      : undefined;
  const watchedFilterFieldValue = useSubscribeField(watchedFilterFieldName);

  const hasUiFilter =
    !disableFiltering &&
    Boolean(dynamicDatasource?.filter) &&
    isUiFilter(dynamicDatasource?.filter) &&
    Boolean(dynamicDatasource?.filter?.formField?.trim());

  const backendSearchParams = useMemo(() => {
    if (
      !enableBackendSearch ||
      !trimmedOptionSearchQuery ||
      !isDynamicReady ||
      !labelKey ||
      !valueKey
    ) {
      return undefined;
    }

    return Array.from(new Set([labelKey, valueKey])).reduce<
      Record<string, string>
    >((acc, key) => {
      acc[key] = trimmedOptionSearchQuery;
      return acc;
    }, {});
  }, [
    enableBackendSearch,
    isDynamicReady,
    labelKey,
    trimmedOptionSearchQuery,
    valueKey,
  ]);

  const filterExpressionCode =
    !disableFiltering &&
    dynamicDatasource?.filter &&
    isExpressionFilter(dynamicDatasource.filter)
      ? (dynamicDatasource.filter.expression?.trim() ?? "")
      : "";
  const expressionDepFieldNames = useMemo(
    () =>
      filterExpressionCode
        ? extractReferencedFieldNames(filterExpressionCode)
        : [],
    [filterExpressionCode],
  );
  const expressionDepsTick = useSubscribeFields(expressionDepFieldNames);
  const expressionMasterDataTableKeys = useMemo(
    () =>
      filterExpressionCode
        ? extractReferencedMasterDataTableNames(filterExpressionCode)
        : [],
    [filterExpressionCode],
  );
  const { cache: expressionMasterDataCache } = usePreloadMasterDataTableKeys(
    expressionMasterDataTableKeys,
  );
  const isFilterExpressionMasterDataLoading =
    expressionMasterDataTableKeys.some((tableKey) => {
      const status = expressionMasterDataCache[tableKey]?.status;
      return !status || status === "idle" || status === "loading";
    });
  const filterExpressionMasterDataStateKey = expressionMasterDataTableKeys
    .map((tableKey) => {
      const cacheItem = expressionMasterDataCache[tableKey];
      return `${tableKey}:${cacheItem?.status ?? "idle"}:${cacheItem?.records.length ?? 0}`;
    })
    .join("|");

  const evaluateFilterExpression = useCallback(
    (expression: string, row: Record<string, unknown>): boolean => {
      const trimmed = expression.trim();
      if (!trimmed) return true;

      try {
        let codeToExecute = "";
        if (/^function\s+filter\s*\(/.test(trimmed)) {
          codeToExecute = `function() { ${trimmed}\nreturn filter(row); }`;
        } else {
          codeToExecute = `function() { return Boolean(${trimmed}); }`;
        }

        const runFn = executeCode(codeToExecute, [
          { name: "row", value: row },
        ]) as () => unknown;

        return Boolean(runFn());
      } catch (error) {
        console.warn("[SelectField] filter expression error", {
          fieldName,
          tableKey,
          labelKey,
          valueKey,
          selectedValue,
          watchedFilterFieldName,
          watchedFilterFieldValue,
          expression: trimmed,
          row,
          error,
        });
        return false;
      }
    },
    [
      executeCode,
      fieldName,
      labelKey,
      selectedValue,
      tableKey,
      valueKey,
      watchedFilterFieldName,
      watchedFilterFieldValue,
    ],
  );

  const dynamicQueryParams = useMemo(() => {
    if (!isDynamicReady || !labelKey || !valueKey) return undefined;

    const isExpFilter =
      !disableFiltering && isExpressionFilter(dynamicDatasource?.filter);

    const filterColumn =
      !disableFiltering &&
      dynamicDatasource?.filter &&
      isUiFilter(dynamicDatasource.filter)
        ? dynamicDatasource.filter.columnKey
        : undefined;

    const select = isExpFilter
      ? undefined
      : Array.from(
          new Set(
            [labelKey, valueKey, filterColumn].filter((v): v is string =>
              Boolean(v),
            ),
          ),
        );

    return {
      limit: 1000,
      sortBy: dynamicDatasource?.sorter?.columnKey,
      sortOrder: dynamicDatasource?.sorter?.order,
      search: backendSearchParams,
      select,
    };
  }, [
    backendSearchParams,
    disableFiltering,
    dynamicDatasource?.filter,
    dynamicDatasource?.sorter?.columnKey,
    dynamicDatasource?.sorter?.order,
    isDynamicReady,
    labelKey,
    valueKey,
  ]);

  const {
    records: dynamicRecords,
    isLoading: isDynamicLoading,
    error: dynamicError,
  } = useBpmDatasetRecords(
    isDynamicReady && tableKey ? tableKey : undefined,
    dynamicQueryParams,
  );

  const latestLoadedDynamicOptionsRef = useRef<SelectOption<string>[]>([]);

  const dynamicOptions = useMemo<SelectOption<string>[]>(() => {
    if (!isDynamicReady || !labelKey || !valueKey) return [];
    const expression = isExpressionFilter(dynamicDatasource?.filter)
      ? (dynamicDatasource!.filter.expression?.trim() ?? "")
      : undefined;

    if (expression && isFilterExpressionMasterDataLoading) {
      return [];
    }

    const filteredRows = (dynamicRecords as Record<string, unknown>[]).filter(
      (row) => {
        if (disableFiltering) {
          return true;
        }

        const filter = dynamicDatasource?.filter;
        if (!filter) {
          return true;
        }

        if (
          isUiFilter(filter) &&
          !isEmptyFilterValue(watchedFilterFieldValue)
        ) {
          if (!filter.columnKey || !filter.formField) return true;
          const formValue = watchedFilterFieldValue;
          if (isEmptyFilterValue(formValue)) return true;
          const rowValue = row[filter.columnKey];
          const matched = compareWithOperator(
            filter.logic,
            rowValue,
            formValue,
          );

          return matched;
        }

        if (isExpressionFilter(filter)) {
          if (expression === undefined) return false;
          return evaluateFilterExpression(expression, row);
        }

        return true;
      },
    );

    const mappedOptions = mapRecordsToOptions(filteredRows, labelKey, valueKey);

    return mappedOptions;
  }, [
    dynamicDatasource?.filter,
    dynamicRecords,
    disableFiltering,
    evaluateFilterExpression,
    expressionDepsTick,
    filterExpressionMasterDataStateKey,
    fieldName,
    isFilterExpressionMasterDataLoading,
    isDynamicReady,
    labelKey,
    valueKey,
    watchedFilterFieldName,
    watchedFilterFieldValue,
  ]);

  useEffect(() => {
    if (isDynamicLoading) return;
    latestLoadedDynamicOptionsRef.current = dynamicOptions;
  }, [dynamicOptions, isDynamicLoading]);

  const effectiveDynamicOptions = useMemo(
    () =>
      isDynamicLoading ? latestLoadedDynamicOptionsRef.current : dynamicOptions,
    [dynamicOptions, isDynamicLoading],
  );

  const options: SelectOption<string>[] = useMemo(() => {
    const base =
      datasource?.type === "static"
        ? (datasource.options ?? [])
        : datasource?.type === "dynamic"
          ? effectiveDynamicOptions
          : [];

    if (datasource?.displayOption !== "label_value") return base;
    return base.map((opt) => ({
      ...opt,
      label: `${opt.label} - ${opt.value}`,
    }));
  }, [datasource, effectiveDynamicOptions]);

  const selectedOptionValues = useMemo(
    () =>
      resolveSelectedOptionValues(selectedValue, normalizedDefault)
        .map(normalizeSelectValue)
        .filter(Boolean),
    [normalizedDefault, selectedValue],
  );
  const selectedLookupValues = useMemo(
    () => Array.from(new Set(selectedOptionValues)),
    [selectedOptionValues],
  );
  const existingOptionValues = useMemo(
    () => new Set(options.map((option) => normalizeSelectValue(option.value))),
    [options],
  );
  const selectedLookupRequests = useMemo(() => {
    if (
      datasource?.type !== "dynamic" ||
      !isDynamicReady ||
      !tableKey ||
      !labelKey ||
      !valueKey
    ) {
      return [];
    }

    return selectedLookupValues
      .filter((value) => !existingOptionValues.has(value))
      .map((value) => ({
        code: tableKey,
        value,
        valueKey,
        select: Array.from(new Set([labelKey, valueKey])),
      }));
  }, [
    datasource?.type,
    existingOptionValues,
    isDynamicReady,
    labelKey,
    selectedLookupValues,
    tableKey,
    valueKey,
  ]);
  const { records: selectedLookupRecords, isLoading: isSelectedLookupLoading } =
    useBpmDatasetRecordsByValues(selectedLookupRequests);
  const selectedLookupOptions = useMemo(() => {
    if (datasource?.type !== "dynamic" || !labelKey || !valueKey) return [];

    const lookupOptions = mapRecordsToOptions(
      selectedLookupRecords as Record<string, unknown>[],
      labelKey,
      valueKey,
    ).filter((option) =>
      selectedLookupValues.includes(normalizeSelectValue(option.value)),
    );

    if (datasource.displayOption !== "label_value") return lookupOptions;
    return lookupOptions.map((option) => ({
      ...option,
      label: `${option.label} - ${option.value}`,
    }));
  }, [
    datasource?.type,
    datasource?.displayOption,
    labelKey,
    selectedLookupRecords,
    selectedLookupValues,
    valueKey,
  ]);

  const optionCacheRef = useRef<Record<string, SelectOption<string>>>({});

  useEffect(() => {
    if (datasource?.type !== "dynamic" || options.length === 0) return;

    options.forEach((option) => {
      optionCacheRef.current[normalizeSelectValue(option.value)] = option;
    });
  }, [datasource?.type, options]);

  const optionsWithSelectedValues = useMemo(() => {
    if (datasource?.type !== "dynamic" || selectedOptionValues.length === 0) {
      return options;
    }

    const existingValues = new Set(
      options.map((option) => normalizeSelectValue(option.value)),
    );
    const selectedOptions = selectedOptionValues
      .map((value) => optionCacheRef.current[value])
      .filter(
        (option): option is SelectOption<string> =>
          Boolean(option) &&
          !existingValues.has(normalizeSelectValue(option.value)),
      );

    const mergedOptions = [
      ...selectedLookupOptions,
      ...selectedOptions,
      ...options,
    ];
    const seen = new Set<string>();

    return mergedOptions.filter((option) => {
      const value = normalizeSelectValue(option.value);
      if (seen.has(value)) return false;
      seen.add(value);
      return true;
    });
  }, [datasource?.type, options, selectedLookupOptions, selectedOptionValues]);

  const missingSelectedValues = useMemo(() => {
    if (datasource?.type !== "dynamic") return [];
    if (
      isDynamicLoading ||
      isFilterExpressionMasterDataLoading ||
      isSelectedLookupLoading
    ) {
      return [];
    }

    const renderedValues = optionsWithSelectedValues.map((option) =>
      normalizeSelectValue(option.value),
    );

    return selectedOptionValues.filter(
      (value) => !renderedValues.includes(value),
    );
  }, [
    datasource?.type,
    isDynamicLoading,
    isFilterExpressionMasterDataLoading,
    isSelectedLookupLoading,
    optionsWithSelectedValues,
    selectedOptionValues,
  ]);

  useEffect(() => {
    if (datasource?.type !== "dynamic") return;

    const optionValues = options.map((option) =>
      normalizeSelectValue(option.value),
    );

    if (missingSelectedValues.length > 0) {
      console.warn("[SelectFieldDebug] selected value missing from options", {
        fieldName,
        tableKey,
        selectedValue,
        selectedOptionValues,
        missingSelectedValues,
        rawOptionCount: options.length,
        renderedOptionCount: optionsWithSelectedValues.length,
        dynamicRecordCount: dynamicRecords.length,
        dynamicOptionCount: dynamicOptions.length,
        isDynamicLoading,
        isFilterExpressionMasterDataLoading,
        isSelectedLookupLoading,
        backendSearchParams,
        optionValuesPreview: optionValues.slice(0, 20),
        optionPreview: options.slice(0, 5).map((option) => ({
          value: option.value,
          label: String(option.label),
        })),
      });
    }
  }, [
    datasource?.type,
    dynamicOptions.length,
    dynamicRecords.length,
    fieldName,
    backendSearchParams,
    isDynamicLoading,
    isFilterExpressionMasterDataLoading,
    isSelectedLookupLoading,
    missingSelectedValues,
    options,
    optionsWithSelectedValues,
    selectedOptionValues,
    selectedValue,
    tableKey,
  ]);

  return {
    options: optionsWithSelectedValues,
    isDynamicLoading: isDynamicLoading || isFilterExpressionMasterDataLoading,
    dynamicError,
    dynamicOptions,
    watchedFilterFieldName,
    watchedFilterFieldValue,
    hasUiFilter,
    missingSelectedValues,
  };
}
